import {
  useEffectWithController,
  useLoadableState
} from '@carvana/merch-react-hooks';
import { isSuccess } from '@carvana/merch-result';
import {
  calculateGetItBy,
  calculateSoonestAvailable
} from '@carvana/merch-tile';
import ClientId from '~/constants/ClientId';
import { useEventCorrelationId } from '~/context/common/StaticContext';
import { useUserId, useUserLocation } from '~/context/common/UserContext';
import type { MatchingVehicleWithDeltas } from '~/hooks/new-cars/selectors/useMatchingVehiclesWithDeltas';
import fetchVehiclesDeliveryPickup from '~/services/common/fetchVehiclesDeliveryPickup';
import { generateErrorLogger } from '~/utilities/loggers';

const GET_IT_BY_OPTIONS = {
  ordinalDay: false,
  sentenceCase: true,
  truncateDay: true
} as const;

export default function useMatchingVehiclesDeliveryPickup(
  vehicles: MatchingVehicleWithDeltas[]
) {
  const correlationId = useEventCorrelationId();
  const userId = useUserId();
  const { zip } = useUserLocation();

  const fulfillment = useLoadableState<Record<number, string>>({});
  const { setData, setError, setLoading } = fulfillment;

  useEffectWithController(
    controller => {
      if (!zip || vehicles.length === 0) return;

      setLoading();

      fetchVehiclesDeliveryPickup(
        {
          clientId: ClientId.SRP_UI, // TODO: ask them to add new_cars
          correlationId,
          userId,
          vehicles: vehicles.map(vehicle => ({
            vehicleId: vehicle.vehicleId,
            vehicleInventoryType: vehicle.fulfillmentType,
            vehicleLocationId: vehicle.locationId,
            vehicleSaleStatus: vehicle.isPurchasePending ? '' : 'Available'
          })),
          zip
        },
        controller
      )
        .then(({ data: response }) => {
          if (!isSuccess(response)) {
            throw new Error(response.errorMessage);
          }

          setData(
            response.content.reduce<Record<number, string>>((acc, content) => {
              const delivery = isSuccess(content.soonestDelivery)
                ? content.soonestDelivery.content
                : undefined;
              const pickup = isSuccess(content.soonestPickup)
                ? content.soonestPickup.content
                : undefined;

              acc[content.vehicleId] = calculateGetItBy(
                calculateSoonestAvailable({ delivery, pickup }),
                GET_IT_BY_OPTIONS
              ).getItBy;

              return acc;
            }, {})
          );
        })
        .catch(
          generateErrorLogger(
            'Get matching vehicles delivery & pickup info',
            {},
            { callback: setError, controller }
          )
        );
    },
    [correlationId, setData, setError, setLoading, userId, vehicles, zip]
  );

  return fulfillment;
}
